import {browserConnectionIdentity} from '../nativeTools/browserConnection';
import type {CreatorBrowserBinding} from './creator-browser';
import type { SourceContext, SourceTypeDefinition } from '../core/definitions';
import {ensureBrowserConnection,clearBrowserConnection,subscribeBrowserConnection,browserConnectionState} from '../nativeTools/browserConnection';
import type {BrowserLeaseClearCause,LocalBrowserGrant} from '../nativeTools/browserAutoLink';
import { parseBrowserPacket, type BrowserVideoConfig, type BrowserH264Packet } from '../nativeTools/browserWire';
import {createBrowserVideoDecoder} from '../nativeTools/browserVideoDecoder';
import {supportsBrowserLossless} from '../nativeTools/browserLossless';
import {BrowserLosslessWorker} from '../nativeTools/browserLosslessWorker';
import {BrowserMediaClock} from '../nativeTools/browserMediaClock';
import { BaseSourceRuntime } from './base';
export type BrowserSettings = { url: string; width: number; height: number; videoTransport?: 'auto' | 'lossless' | 'h264'; port?: number };
export function validateBrowserSettings(s: BrowserSettings): BrowserSettings {
  if (
    !Number.isInteger(s.width) ||
    s.width < 1 ||
    s.width > 3840 ||
    !Number.isInteger(s.height) ||
    s.height < 1 ||
    s.height > 2160
  )
    throw new Error('Browser size must be between 1×1 and 3840×2160.');
  const url = new URL(s.url);
  if (
    !['http:', 'https:', 'omp-html:'].includes(url.protocol) ||
    url.username ||
    url.password ||
    !url.hostname ||
    s.url.length > 2048 ||
    /[\x00-\x1f\x7f\\]/.test(decodeURIComponent(s.url))
  )
    throw new Error('Use an HTTP(S) URL or an imported HTML page.');
  if (
    url.protocol === 'omp-html:' &&
    (url.hostname !== 'import' || !url.pathname.startsWith('/') || url.pathname.includes('..'))
  )
    throw new Error('Invalid imported HTML path.');
  for (const key of url.searchParams.keys())
    if (/token|password|secret|authorization|credential|stream_key/i.test(key) || /^(code|grant)$/i.test(key))
      throw new Error('Credentials cannot be sent to Browser pages.');
  if (s.videoTransport !== undefined && s.videoTransport !== 'auto' && s.videoTransport !== 'lossless' && s.videoTransport !== 'h264')
    throw new Error('Invalid Browser video transport.');
  return {url:url.href,width:s.width,height:s.height,videoTransport:s.videoTransport ?? 'auto'};
}
export class BrowserSequenceGate {
  private frame = 0;
  private audio = 0;
  constructor(
    private readonly id: string,
    private width: number,
    private height: number,
  ) {}
  reset(): void {
    this.frame = 0;
    this.audio = 0;
  }
  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
  }
  accept(h: Record<string, unknown>): boolean {
    if (h.browser_id !== this.id || typeof h.sequence !== 'number') return false;
    if (h.type === 'browser.frame' || h.type === 'browser.video-config') {
      if (h.width !== this.width || h.height !== this.height || h.sequence <= this.frame) return false;
      this.frame = h.sequence;
      return true;
    }
    if (h.type === 'browser.audio' && h.sequence > this.audio) {
      this.audio = h.sequence;
      return true;
    }
    return false;
  }
}
/** WebSocket open + browser.ready may include a slow backend link and a CEF cold start. */
export const BROWSER_READY_TIMEOUT_MS = 15000;
/** Reconnect back-off; resets after browser.ready so a slow backend cannot cause a storm. */
export const BROWSER_RETRY_DELAYS_MS = [2000, 3000, 5000] as const;
export class BrowserRuntime extends BaseSourceRuntime<BrowserSettings> {
  private settings: BrowserSettings;
  private ws: WebSocket | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private deadline: ReturnType<typeof setTimeout> | null = null;
  private retries = 0;
  private readonly canvas: HTMLCanvasElement;
  private readonly gain: GainNode;
  private readonly gate: BrowserSequenceGate;
  private readonly id = `browser_${crypto.randomUUID().replaceAll('-', '')}`;
  private readonly unsubscribe: () => void;
  private activeDecode: object | null = null;
  private losslessDecoder = new BrowserLosslessWorker();
  private pendingFrame: ReturnType<typeof parseBrowserPacket> | null = null;
  private ready = false;
  private videoDecoder: ReturnType<typeof createBrowserVideoDecoder> | null = null;
  private ownedFrame: VideoFrame | null = null;
  private h264Negotiated = false;
  private losslessNegotiated = false;
  private forceLossless = false;
  private readonly mediaClock = new BrowserMediaClock();
  private transport = 'png';
  private decodedFrames = 0;
  private decodedPending: VideoFrame[] = [];
  private decodedIntervalMs = 1000/60;
  private decodedLastTimestamp: number | null = null;
  private rasterPending: {timestamp:number;paint:()=>void;close:()=>void}[] = [];
  private playoutStarted = false;
  private playoutDeadline = 0;
  private droppedDecoded = 0;
  private droppedRaster = 0;
  private presentedFrames = 0;
  private lastPresentedNativeMs: number | null = null;
  private recoveryCount = 0;
  private recoveryTimer: ReturnType<typeof setTimeout> | null = null;
  private lastKeyframeAt = -Infinity;
  private presentationEpoch = 0;
  private address = '';
  private readonly revokedDestinations = new Set<string>();
  private readonly networkDecisions = new Map<string, {code: string; host: string; port: number}>();
  private network: {code: string; host: string; port: number} | null = null;
  private readonly lifetime=new AbortController();

  // Lifetime aggregates only; no payloads, URLs, tokens or session identifiers.
  private readonly audioDiagnostics = {
    packets: 0, scheduled: 0, discontinuities: 0, underruns: 0, rebases: 0,
    queueDrops: 0, obsoleteDrops: 0, inactiveDrops: 0, maxNativeJumpMs: 0, maxArrivalGapMs: 0,
  };
  private lastAudioArrival: number | null = null;
  private audioArrivalFloor: number | null = null;
  private audioJitterMs = 0;
  private audioReserveMs = 0;
  private lastLongAudioGapAt = -Infinity;
  private disconnects = 0;
  private disconnectCause: 'none' | 'socket-close' | 'socket-error' | 'ready-timeout' | 'protocol-error' | 'decode-error' | 'page-refused' | 'lease-cleared' | 'decoder-unavailable' | 'recovery-exhausted' = 'none';
  private disconnectCode = 0;
  private packetStage: 'none' | 'decode' | 'header' | 'sequence' | 'address' | 'audio' | 'video-config' | 'video' | 'raster' = 'none';
  private packetType: 'unknown' | 'browser.ready' | 'browser.audio' | 'browser.frame' | 'browser.video-config' = 'unknown';
  private packetErrorKind: 'none' | 'Error' | 'TypeError' | 'RangeError' | 'DOMException' | 'Other' = 'none';
  private leaseClearCause: BrowserLeaseClearCause | 'none' = 'none';
  private audioUntil = 0;
  private pendingAudioRebase: {receivedAt:number;started:number;deadline:number;header:Record<string,unknown>;payload:Uint8Array}|null=null;
  private expectedAudioMs: number | null = null;
  private audioClockStopped = false;
  private audioClockObservation: {time:number;advancedAt:number} | null = null;
  private readonly scheduled = new Set<AudioBufferSourceNode>();
  private readonly audioStateChanged=():void=>{
    if(this.ctx.audioContext.state==='running')return;
    this.clearAudio(true);
    for(const frame of this.decodedPending)frame.close();
    this.decodedPending=[];
    for(const frame of this.rasterPending)frame.close();
    this.rasterPending=[];
    this.playoutStarted=false;
  };
  constructor(ctx: SourceContext, settings: BrowserSettings,private readonly creator?:CreatorBrowserBinding) {
    super(ctx);
    this.settings = { ...settings };
    this.canvas = document.createElement('canvas');
    this.gain = ctx.audioContext.createGain();
    ctx.audioContext.addEventListener?.('statechange',this.audioStateChanged);
    this.gate = new BrowserSequenceGate(this.id, settings.width, settings.height);
    this.unsubscribe = subscribeBrowserConnection(() => {
      const state = browserConnectionState();
      if (!this.destroyed && this.ws && !state.grant) {
        this.leaseClearCause = state.clearCause ?? 'explicit-clear';
        this.recordDisconnect('lease-cleared');
        this.connect();
      }
    });
    this.connect();
  }
  update(settings: BrowserSettings): void {
    if(this.creator){this.creator.retire('browser_update');return;}
    if (this.destroyed) return;
    if (JSON.stringify(settings) === JSON.stringify(this.settings)) return;
    let next: BrowserSettings;
    try {
      next = validateBrowserSettings(settings);
    } catch (error) {
      this.beginEpoch();
      this.clearConnection();
      this.settings = { ...settings };
      this.fail('load-failed', (error as Error).message);
      return;
    }
    const previous = this.settings;
    if (previous.url !== next.url) { this.resetNetworkStatus(); }
    this.settings = next;
    if ((previous.videoTransport ?? 'auto') !== next.videoTransport) {
      this.forceLossless = false;
      this.connect();
      return;
    }
    if (
      this.ready &&
      previous.url.startsWith('omp-html:') === next.url.startsWith('omp-html:')
    ) {
      if (previous.width !== next.width || previous.height !== next.height) {
        this.invalidatePresentation();
        this.gate.resize(next.width, next.height);
        this.setStatus('starting');
        this.send({ op: 'resize', width: next.width, height: next.height });
      }
      if (previous.url !== next.url) {
        this.invalidatePresentation();
        this.send({ op: 'navigate', url: next.url });
      }
    } else this.connect();
  }
  getLiveInfo(): Record<string, string> {
    const audio = this.audioDiagnostics;
    const video = this.getVideo();
    const presentedSize = video ? `${video.width}×${video.height}` : 'unknown';
    const queueMs = (Math.max(0, this.audioUntil - this.ctx.audioContext.currentTime) * 1000).toFixed(3);
    return {
      address: this.address || this.settings.url, transport: this.transport,
      decodedFrames: String(this.decodedFrames), recoveries: String(this.recoveryCount),
      retainedVideoFrames: String(this.decodedPending.length + this.rasterPending.length + (this.ownedFrame ? 1 : 0)), droppedDecoded: String(this.droppedDecoded),
      presentedSize, presentedFrames: String(this.presentedFrames), droppedRaster: String(this.droppedRaster),
      videoPresentation: `${this.presentedFrames} presented (${presentedSize}); ${this.decodedPending.length + this.rasterPending.length + (this.ownedFrame ? 1 : 0)} retained; ${this.droppedDecoded} decoded / ${this.droppedRaster} raster dropped`,
      audioPackets: String(audio.packets), audioScheduled: String(audio.scheduled),
      audioDiscontinuities: String(audio.discontinuities), audioUnderruns: String(audio.underruns), audioRebases: String(audio.rebases),
      audioQueueDrops: String(audio.queueDrops), audioObsoleteDrops: String(audio.obsoleteDrops), audioInactiveDrops: String(audio.inactiveDrops),
      audioMaxNativeJumpMs: audio.maxNativeJumpMs.toFixed(3), audioMaxArrivalGapMs: audio.maxArrivalGapMs.toFixed(3),
      audioQueueMs: queueMs, audioJitterMs: this.audioJitterMs.toFixed(3), audioPlayoutReserveMs: this.audioReserveMs.toFixed(3),
      diagnosticsSampleMs: performance.now().toFixed(3),
      audioFlow: `${audio.packets} received, ${audio.scheduled} scheduled, ${queueMs} ms queued`,
      audioRecovery: `${audio.discontinuities} discontinuities, ${audio.underruns} underruns, ${audio.rebases} rebases`,
      audioDrops: `${audio.queueDrops} queue, ${audio.obsoleteDrops} obsolete, ${audio.inactiveDrops} inactive`,
      audioTiming: `${audio.maxNativeJumpMs.toFixed(3)} ms native jump, ${audio.maxArrivalGapMs.toFixed(3)} ms arrival gap; ${this.audioJitterMs.toFixed(3)} ms jitter, ${this.audioReserveMs.toFixed(3)} ms reserve`,
      connectionDiagnostic: `${this.disconnects} disconnects; ${this.disconnectCause} (${this.disconnectCode}); lease ${this.leaseClearCause}; packet ${this.packetStage}/${this.packetType}/${this.packetErrorKind}`,
      disconnects: String(this.disconnects), disconnectCause: this.disconnectCause, disconnectCode: String(this.disconnectCode),
      networkCode: this.network?.code ?? '',
      leaseClearCause: this.leaseClearCause, packetStage: this.packetStage, packetType: this.packetType, packetErrorKind: this.packetErrorKind,
    };
  }
  invoke(action: string, args?: unknown): void {
    if(this.creator){this.creator.retire('browser_invoke');return;}
    if (this.destroyed) return;
    if (action === 'retry') this.connect();
    if(action==='reload'&&!this.ready){this.connect();return;}
    if (action === 'reload' || action === 'back' || action === 'forward') {
      this.invalidatePresentation();
      this.send({ op: action });
    }
    if (action === 'browser-input' && args && typeof args === 'object') this.send({ ...args, op: 'input' });
  }
  private invalidatePresentation(clear = true, resetTransport = false): void {
    if (this.recoveryTimer) clearTimeout(this.recoveryTimer);
    this.recoveryTimer = null;
    this.presentationEpoch++;
    this.activeDecode = null;
    this.pendingFrame = null;
    this.videoDecoder?.dispose();
    this.videoDecoder = null;
    for (const frame of this.decodedPending) frame.close();
    this.decodedPending = [];
    this.decodedLastTimestamp = null;
    for (const frame of this.rasterPending) frame.close();
    this.rasterPending = [];
    this.lastPresentedNativeMs = null;
    this.playoutStarted = false;
    this.playoutDeadline = 0;
    if (clear) {
      this.clearAudio(resetTransport);
      this.clearVideo();
      this.ownedFrame?.close();
      this.ownedFrame = null;
    }
  }
  /** With PCM, native picture timestamps follow the scheduled sample clock.
   * Silent pages retain the two-picture decoder jitter buffer. Decoded pictures
   * are independent; a late picture can be dropped without dropping codec input. */
  tick(): void {
    if(this.creator&&!this.creator.current()){this.creator.retire('browser_current');return;}
    const recovery=this.pendingAudioRebase;
    if(recovery) {
      const now=performance.now(),nativeMs=Number(recovery.header.monotonic_ms);
      const packetMs=Number(recovery.header.frames)/Number(recovery.header.sample_rate)*1000;
      // A timer cannot prove that queued WebSocket delivery has drained. Admit
      // a fresh capture point, measured against the least-delayed transport
      // observation, before rebinding both tracks. A persistent path-delay
      // change gets a new observation after bounded recovery, never a mute loop.
      if(now>=recovery.deadline&&(this.mediaClock.captureAgeMs(nativeMs,now)<=packetMs||(now-recovery.started>=200&&now-recovery.receivedAt<=packetMs))) {
        if(now-recovery.started>=200)this.mediaClock.rebaseTransport(nativeMs,now);
        this.pendingAudioRebase=null;this.scheduleAudio(recovery.header,recovery.payload,true);
      }
    }
    if (this.mediaClock.hasAudio) {
      const now=performance.now(),audioNow=this.ctx.audioContext.currentTime;
      const observation=this.audioClockObservation;
      if(!observation||observation.time!==audioNow)this.audioClockObservation={time:audioNow,advancedAt:now};
      else if(now-observation.advancedAt>50) {
        // A device can stop rendering while AudioContext still says running.
        // No audible sample clock exists then: cancel both pending timelines
        // and keep the browser page responsive until that device advances.
        this.clearAudio(true);
        for(const pending of this.decodedPending)pending.close();this.decodedPending=[];
        for(const pending of this.rasterPending)pending.close();this.rasterPending=[];
        this.audioClockStopped=true;
        this.playoutStarted=true;
        this.audioClockObservation={time:audioNow,advancedAt:now};
        return;
      }
      // Preserve FIFO cadence across sub-frame capture/render jitter. Skipping
      // to the newest merely-due picture causes alternating holds and double
      // dequeues at equal source/display rates. Recover only genuine backlog.
      const halfPicture=Math.min(1000/60,this.decodedIntervalMs/2);
      // Program audio leaves through MediaStreamAudioDestinationNode. Its
      // sample clock is currentTime; hardware output timestamps belong to a
      // separate monitor destination and must not delay the program video.
      const deadline=(frame:VideoFrame)=>this.mediaClock.videoTime(frame.timestamp/1000,now,audioNow);
      while(this.decodedPending.length>1 && deadline(this.decodedPending[0]!)<now-this.decodedIntervalMs && deadline(this.decodedPending[1]!)<=now+halfPicture) {
        this.decodedPending.shift()!.close();this.droppedDecoded++;
      }
      if(this.decodedPending.length && deadline(this.decodedPending[0]!)<=now+halfPicture)this.publishDecoded(this.decodedPending.shift()!);
      const rasterDeadline=(frame:typeof this.rasterPending[number])=>this.mediaClock.videoTime(frame.timestamp,now,audioNow);
      while(this.rasterPending.length>1 && rasterDeadline(this.rasterPending[0]!)<now-this.decodedIntervalMs*2 && rasterDeadline(this.rasterPending[1]!)<=now+halfPicture) {
        this.rasterPending.shift()!.close();this.droppedRaster++;
      }
      // When several pictures are queued, admit the next one within half a
      // display interval; leave a short queue on the exact audio deadline.
      const rasterLead=this.rasterPending.length>=3?halfPicture:0.001;
      const raster=this.rasterPending.length && rasterDeadline(this.rasterPending[0]!)<=now+rasterLead?this.rasterPending.shift():undefined;
      if(raster){try{raster.paint();}finally{raster.close();}}
      return;
    }
    if(this.audioClockStopped&&this.audioClockObservation?.time!==this.ctx.audioContext.currentTime) {
      this.audioClockStopped=false;this.audioClockObservation=null;
    }
    if (!this.playoutStarted && this.decodedPending.length < 2 &&
        (!this.decodedPending.length || performance.now() < this.playoutDeadline)) return;
    this.playoutStarted = true;
    const frame = this.decodedPending.shift();
    if (frame) this.publishDecoded(frame);
  }
  private publishDecoded(frame: VideoFrame): void {
    if(this.creator&&!this.creator.frameCurrent(frame)){frame.close();return;}
    this.creator?.presented(frame);
    const previous = this.ownedFrame;
    this.ownedFrame = frame;
    const provider = this.getVideo();
    if (provider && provider.width === frame.displayWidth && provider.height === frame.displayHeight) this.replaceSource(frame);
    else this.publishVideo(frame, frame.displayWidth, frame.displayHeight, false);
    const published=this.getVideo();if(published&&this.creator)published.creatorObservation=this.creator.observeVideo();
    previous?.close();
    this.presentedFrames++;
    this.setStatus('live');
  }
  private configureVideo(packet: ReturnType<typeof parseBrowserPacket>, epoch: number): void {
    this.invalidatePresentation(false);
    this.creator?.codecReset();
    this.transport = 'h264';
    const presentation = this.presentationEpoch;
    const current = (): boolean => this.isCurrent(epoch) && presentation === this.presentationEpoch && this.videoDecoder === decoder && (!this.creator||this.creator.packetCurrent(packet.header));
    let recoveryAttempts = 0;
    const request = (): void => {
      this.recoveryTimer = null;
      if (!current()) return;
      // Requests may be lost to WebSocket backpressure or coalesced at either
      // native hop. Retry at a bounded rate until decoded output confirms it.
      if (++recoveryAttempts > 10) {this.recordDisconnect('recovery-exhausted');this.forceLossless = true; this.connect(); return;}
      this.lastKeyframeAt = performance.now();
      this.send({op:'request-keyframe'});
      this.recoveryTimer = setTimeout(request, 500);
    };
    const decoder = createBrowserVideoDecoder({
      onFrame: frame => {
        if (!current() || frame.displayWidth !== this.settings.width || frame.displayHeight !== this.settings.height) {frame.close(); return;}
        if(this.creator&&!this.creator.decoded(frame)){frame.close();return;}
        if (this.recoveryTimer) clearTimeout(this.recoveryTimer);
        this.recoveryTimer = null;
        this.decodedFrames++;
        if(this.decodedLastTimestamp!==null) {
          const interval=(frame.timestamp-this.decodedLastTimestamp)/1000;
          if(interval>0&&interval<100)this.decodedIntervalMs+=(interval-this.decodedIntervalMs)*0.1;
        }
        this.decodedLastTimestamp=frame.timestamp;
        if (!this.ownedFrame && !this.mediaClock.hasAudio) this.publishDecoded(frame);
        else {
          if (!this.decodedPending.length && !this.playoutStarted) this.playoutDeadline = performance.now() + 25;
          this.decodedPending.push(frame);
          if (this.decodedPending.length > this.videoPendingLimit()) {this.decodedPending.shift()!.close(); this.droppedDecoded++;}
        }
      },
      onRecovery: () => {
        if (!current()) return;
        this.recoveryCount++;
        if (this.recoveryTimer) clearTimeout(this.recoveryTimer);
        const delay = Math.max(0, 251 - (performance.now() - this.lastKeyframeAt));
        if (delay) this.recoveryTimer = setTimeout(request, delay);
        else request();
      },
      onUnavailable: () => {
        if (!current()) return;
        this.recordDisconnect('decoder-unavailable');
        this.forceLossless = true;
        this.connect();
      },
    }, this.creator ? 'per-kind' : 'shared');
    this.videoDecoder = decoder;
    // A replacement config can arrive without its IDR. A decoded picture,
    // rather than the config alone, acknowledges successful recovery.
    this.recoveryTimer = setTimeout(request, 500);
    void decoder.configure({...packet.header, payload:packet.payload} as BrowserVideoConfig);
  }
  private send(message: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN && this.ws.bufferedAmount < 8192)
      this.ws.send(JSON.stringify({ ...message, browser_id: this.id }));
  }
  private networkEvent(event: Record<string, unknown>): void {
    if (event.browser_id !== this.id) return;
    const { code, host, port } = event;
    if (event.v !== 1 || Object.keys(event).length !== 6 ||
        !['NETWORK_PERMISSION_REQUIRED', 'NETWORK_BLOCKED', 'NETWORK_PERMISSION_REVOKED', 'NETWORK_PERMISSION_GRANTED'].includes(String(code)) ||
        typeof host !== 'string' || host.length > 253 || !/^[A-Za-z0-9.:[\]-]+$/.test(host) ||
        !Number.isInteger(port) || Number(port) < 1 || Number(port) > 65535) throw new Error('Invalid Browser network event');
    const destinationKey = JSON.stringify([host, port]);
    if (code === 'NETWORK_PERMISSION_GRANTED') {
      const known = this.networkDecisions.delete(destinationKey);
      this.revokedDestinations.delete(destinationKey);
      this.refreshNetworkStatus();
      if (known && this.revokedDestinations.size === 0) this.invoke('reload');
      return;
    }
    this.networkDecisions.set(destinationKey, { code: String(code), host, port: Number(port) });
    if (code === 'NETWORK_PERMISSION_REVOKED') {
      this.revokedDestinations.add(destinationKey);
      this.invalidatePresentation();
      this.canvas.getContext('2d')?.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
    this.refreshNetworkStatus();
  }
  private resetNetworkStatus(): void {
    this.networkDecisions.clear();
    this.revokedDestinations.clear();
    this.refreshNetworkStatus();
  }
  private refreshNetworkStatus(): void {
    this.network = [...this.networkDecisions.values()].at(-1) ?? null;
    if (!this.network) { this.setWarning(null); return; }
    const {code, host, port} = this.network;
    const destination = `${host}:${port}`;
    this.setWarning(code === 'NETWORK_BLOCKED'
      ? `Acceso de red bloqueado: ${destination}. Este destino no se puede autorizar.`
      : code === 'NETWORK_PERMISSION_REVOKED'
        ? `Permiso de red revocado para ${destination}. Revisá los permisos de esta fuente en OMP Tools.`
        : `Esta fuente necesita acceder a ${destination}. Aprobá el permiso de red en el menú de OMP Tools.`);
  }
  private clearConnection(): void {
    this.resetNetworkStatus();
    if (this.timer) clearTimeout(this.timer);
    if (this.heartbeat) clearInterval(this.heartbeat);
    if (this.deadline) clearTimeout(this.deadline);
    this.timer = this.heartbeat = this.deadline = null;
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onopen = ws.onclose = ws.onmessage = ws.onerror = null;
      ws.close();
    }
    this.ready = false;
    this.h264Negotiated = false;
    this.losslessNegotiated = false;
    this.losslessDecoder.dispose();
    this.invalidatePresentation(true, true);
    this.activeDecode = null;
    this.pendingFrame = null;
  }
  private recordPacketFailure(stage: typeof this.packetStage, type: typeof this.packetType, error: unknown): void {
    this.creator?.diagnose(`packet_${stage}`);
    this.packetStage = stage;
    this.packetType = type;
    // Never inspect constructor/name/message: pages or native peers can control them.
    this.packetErrorKind = error instanceof DOMException ? 'DOMException' : error instanceof TypeError ? 'TypeError' :
      error instanceof RangeError ? 'RangeError' : error instanceof Error ? 'Error' : 'Other';
  }
  private recordDisconnect(cause: typeof this.disconnectCause, code = 0): void {
    this.disconnects++;
    this.disconnectCause = cause;
    this.disconnectCode = Number.isInteger(code) && code >= 1000 && code <= 4999 ? code : 0;
    this.ctx.logger.log('info', 'browser-transport', 'Browser transport disconnected', {
      cause: this.disconnectCause, code: this.disconnectCode, leaseCause: this.leaseClearCause,
      packetStage: this.packetStage, packetType: this.packetType, packetErrorKind: this.packetErrorKind,
    });
  }
  private clearAudio(resetTransport = false):void {
    this.creator?.schedules.clear();
    for (const source of this.scheduled) {
      source.onended = null;
      try {
        source.stop();
      } catch {}
      source.disconnect();
    }
    this.scheduled.clear();
    this.audioUntil = 0;
    this.pendingAudioRebase = null;
    this.expectedAudioMs = null;
    this.lastAudioArrival = null;
    this.audioArrivalFloor = null;
    this.lastLongAudioGapAt = performance.now();
    // A native source pause breaks sample continuity, not its existing transport.
    // Exclude the pause from new observations without relearning path protection.
    if (resetTransport) { this.audioJitterMs = 0; this.audioReserveMs = 0; }
    this.audioClockStopped=false;
    this.audioClockObservation=null;
    this.mediaClock.reset();
    this.setAudio(null);
  }
  private creatorConnected=false;
  private connect(): void {if(this.creator&&this.creatorConnected){this.creator.retire('browser_reconnect');return;}this.creatorConnected=true;void this.connectAsync();}
  private retryDelay(floor = 0): number {
    const delay = BROWSER_RETRY_DELAYS_MS[Math.min(this.retries, BROWSER_RETRY_DELAYS_MS.length - 1)]!;
    this.retries++;
    return Math.max(floor, delay);
  }
  private async connectAsync():Promise<void> {
    this.clearConnection();
    const epoch = this.beginEpoch();
    try {
      this.settings = validateBrowserSettings(this.settings);
      if (![this.ctx.projectId, this.ctx.sourceId].every(id => typeof id === 'string' && /^[A-Za-z0-9._:-]{1,128}$/.test(id)))
        throw new Error('Browser requiere un proyecto y una fuente con identificadores válidos');
    } catch (error) {
      this.fail('load-failed', (error as Error).message);
      return;
    }
    this.setStatus('starting');
    let grant:LocalBrowserGrant;
    try{grant=await ensureBrowserConnection(this.lifetime.signal);}catch(error){
      if(this.creator){this.creator.retire('browser_connect_failed');return;}
      if(!this.isCurrent(epoch))return;
      this.fail('permission-denied',(error as Error).message);
      this.timer=setTimeout(()=>{if(this.isCurrent(epoch))this.connect();},this.retryDelay(3000));return;
    }
    if(!this.isCurrent(epoch)||this.creator&&!this.creator.current())return;
    const token=grant.token;
    this.gate.resize(this.settings.width, this.settings.height);
    this.gate.reset();
    this.setStatus('starting');
    if(this.creator){try{const identity=browserConnectionIdentity(grant);if(!identity)throw Error('creator_unavailable');this.creator.acceptConnection(identity);}catch{this.creator.retire('browser_identity');return;}}
    const ws = new WebSocket(`ws://127.0.0.1:${grant.port}/v1/browser`);
    this.ws = ws;
    this.creator?.attach(this.id,value=>{if(ws.readyState!==WebSocket.OPEN||ws.bufferedAmount>=8192)throw Error('creator_unavailable');ws.send(value);},()=>{this.beginEpoch();this.clearConnection();this.fail('not-found','creator_unavailable');});
    ws.binaryType = 'arraybuffer';
    let socketErrored = false;
    const lost = (unauthorized = false, cause: typeof this.disconnectCause = 'socket-close', code = 0): void => {
      if (!this.isCurrent(epoch)) return;
      this.recordDisconnect(cause, code);
      if(this.creator){this.creator.retire('browser_disconnected');return;}
      const retryEpoch = this.beginEpoch();
      this.clearConnection();
      if (unauthorized) {
        clearBrowserConnection('La conexión Browser venció');
      }
      this.fail('not-found', 'ompeer-tools Browser disconnected. Start it again to reconnect.');
      this.timer = setTimeout(() => {
        if (this.isCurrent(retryEpoch)) this.connect();
      }, this.retryDelay());
    };
    this.deadline = setTimeout(() => lost(false, 'ready-timeout'), BROWSER_READY_TIMEOUT_MS);
    ws.onopen = () => {
      if (this.isCurrent(epoch)) ws.send(JSON.stringify({ op: 'auth', token,browserSessionId:grant.browserSessionId }));
    };
    ws.onclose = (event) => {
      if(this.creator){this.creator.retire('browser_socket_close');return;}
      if (event.code === 1007 && this.isCurrent(epoch)) {
        this.recordDisconnect('page-refused', event.code);
        this.beginEpoch();
        this.clearConnection();
        this.fail(
          'load-failed',
          'Browser refused this page or command. Para HTML local, elegí su carpeta en OMP Tools.',
        );
        return;
      }
      lost(event.code === 1008, socketErrored ? 'socket-error' : 'socket-close', event.code);
    };
    ws.onerror = () => { socketErrored = true; ws.close(); };
    ws.onmessage = (event: MessageEvent<unknown>) => {
      if (!this.isCurrent(epoch)) return;
      let stage: typeof this.packetStage = 'decode';
      let type: typeof this.packetType = 'unknown';
      try {
        if (typeof event.data === 'string') {
          stage = 'header';
          if(this.creator&&new TextEncoder().encode(event.data).length>4096)throw Error('creator_unavailable');
          const ready: unknown = JSON.parse(event.data);
          if(this.creator&&ready&&typeof ready==='object'&&(ready as Record<string,unknown>).type!=='browser.ready'){this.creator.text(ready as Record<string,unknown>);const config=this.creator.takeConfig();if(config)this.configureVideo(config,epoch);return;}
          if (ready && typeof ready === 'object' && (ready as Record<string, unknown>).type === 'browser.network-reset') {
            const reset = ready as Record<string, unknown>;
            if (reset.browser_id !== this.id) return;
            if (reset.v !== 1 || Object.keys(reset).length !== 3) throw new Error('Invalid Browser network reset');
            this.resetNetworkStatus();
            this.invalidatePresentation();
            this.canvas.getContext('2d')?.clearRect(0, 0, this.canvas.width, this.canvas.height);
            return;
          }
          if (ready && typeof ready === 'object' && (ready as Record<string, unknown>).type === 'browser.network') {
            this.networkEvent(ready as Record<string, unknown>);
            return;
          }
          if (!ready || typeof ready !== 'object' || (ready as Record<string, unknown>).type !== 'browser.ready')
            throw new Error('Unexpected Browser response');
          type = 'browser.ready';
          if (this.deadline) clearTimeout(this.deadline);
          this.deadline = null;
          this.retries = 0;
          this.ready = true;
          const canUseAvc = (ready as Record<string,unknown>).h264_v1 === true && typeof VideoDecoder !== 'undefined' && !this.forceLossless;
          this.losslessNegotiated = this.losslessDecoder.available && this.settings.videoTransport === 'lossless' && (ready as Record<string,unknown>).rgba8_zstd_v1 === true && supportsBrowserLossless();
          this.h264Negotiated = !this.losslessNegotiated && this.settings.videoTransport !== 'lossless' && canUseAvc;
          // Preserve AVC for motion and the legacy PNG fallback. Pixel-preserving
          // compression remains explicit until its A/V and motion gates pass.
          const format = this.losslessNegotiated ? 'auto-lossless' : this.h264Negotiated ? 'auto-h264' : this.settings.videoTransport === 'lossless' ? 'rgba8' : 'png';
          if(this.creator)this.creator.ready(ready as Record<string,unknown>,format,this.settings.width,this.settings.height);
          else this.send({ op: 'create', project_id: this.ctx.projectId, source_id: this.ctx.sourceId, url: this.settings.url, width: this.settings.width, height: this.settings.height, ...(format === 'png' ? {} : {format}) });
          this.heartbeat = setInterval(() => this.send({ op: 'ping' }), 20000);
          return;
        }
        if (!(event.data instanceof ArrayBuffer)) throw new Error('Invalid Browser transport');
        if (this.revokedDestinations.size) return;
        const packet = parseBrowserPacket(event.data);
        stage = 'header';
        type = packet.header.type;
        stage = 'sequence';
        if(this.creator){if(!this.creator.admitPacket(packet))return;}else if (!this.gate.accept(packet.header)) return;
        stage = 'header';
        this.mediaClock.observe(packet.header.monotonic_ms as number, performance.now());
        if (typeof packet.header.url === 'string' && packet.header.url) {
          stage = 'address';
          this.address = validateBrowserSettings({...this.settings,url:packet.header.url}).url;
        }
        if (packet.header.type === 'browser.audio') {
          stage = 'audio';
          if(this.creator?.takeAudioReset())this.clearAudio(true);
          this.enqueueAudio(packet.header, packet.payload);
          return;
        }
        if (packet.header.type === 'browser.video-config') {
          stage = 'video-config';
          if (!this.h264Negotiated) throw new Error('Unnegotiated Browser codec');
          this.configureVideo(packet, epoch);
        } else if (packet.header.format === 'h264') {
          stage = 'video';
          if (!this.h264Negotiated) throw new Error('Unnegotiated Browser codec');
          this.creator?.videoInput(packet.header);
          this.videoDecoder?.push({...packet.header,payload:packet.payload} as BrowserH264Packet);
        } else {
          stage = 'raster';
          if (this.videoDecoder) this.invalidatePresentation(false);
          if (packet.header.format === 'rgba8-zstd' && !this.losslessNegotiated) throw new Error('Unnegotiated lossless Browser codec');
          this.transport = packet.header.format as 'png' | 'rgba8' | 'rgba8-zstd';
          this.presentFrame(packet, epoch, lost);
        }
      } catch (error) {
        this.recordPacketFailure(stage, type, error);
        lost(false, 'protocol-error');
      }
    };
  }
  /** Keep one in-flight decode and only the newest waiting frame. Never grow a backlog. */
  private presentFrame(packet: ReturnType<typeof parseBrowserPacket>, epoch: number, lost: (unauthorized?: boolean, cause?: typeof this.disconnectCause) => void): void {
    if (this.activeDecode) { this.pendingFrame = packet; return; }
    const token = {};
    this.activeDecode = token;
    const presentation = this.presentationEpoch;
    const current = (): boolean => this.isCurrent(epoch) && presentation === this.presentationEpoch && (!this.creator||this.creator.packetCurrent(packet.header));
    const width = packet.header.width as number, height = packet.header.height as number;
    const paint = (image: CanvasImageSource | ImageData): void => {
      if (!current() || width !== this.settings.width || height !== this.settings.height) return;
      if (typeof packet.header.url === 'string' && packet.header.url)
        this.address = validateBrowserSettings({ ...this.settings, url: packet.header.url }).url;
      // Assigning even an unchanged size clears the canvas and its graphics state.
      if (this.canvas.width !== width) this.canvas.width = width;
      if (this.canvas.height !== height) this.canvas.height = height;
      const context = this.canvas.getContext('2d');
      if (packet.header.format === 'rgba8' || packet.header.format === 'rgba8-zstd') context?.putImageData(image as ImageData, 0, 0);
      else if (context) {
        // A complete native image replaces the previous one, including pixels
        // that became transparent. source-over alone would accumulate alpha.
        context.clearRect(0, 0, width, height);
        context.drawImage(image as CanvasImageSource, 0, 0);
      }
      const previous = this.ownedFrame;
      this.ownedFrame = null;
      this.presentedFrames++;
      this.lastPresentedNativeMs=Number(packet.header.monotonic_ms);
      this.publishCanvas(this.canvas);
      this.creator?.observe(packet.header,'presented');
      const published=this.getVideo();if(published&&this.creator)published.creatorObservation=this.creator.observeVideo();
      previous?.close();
      this.setStatus('live');
    };
    const finish = (): void => {
      if (this.activeDecode !== token) return;
      this.activeDecode = null;
      const next = this.pendingFrame;
      this.pendingFrame = null;
      if (current() && next) this.presentFrame(next, epoch, lost);
    };
    const deliver = (image:CanvasImageSource|ImageData,close:()=>void):void => {
      if(!current()){close();return;}
      if(this.mediaClock.hasAudio) {
        this.rasterPending.push({timestamp:packet.header.monotonic_ms as number,paint:()=>paint(image),close});
        if(this.rasterPending.length>this.videoPendingLimit()) {this.rasterPending.shift()!.close();this.droppedRaster++;}
      } else {try {paint(image);} finally {close();}}
    };
    if (packet.header.format === 'png') {
      void createImageBitmap(new Blob([packet.payload as Uint8Array<ArrayBuffer>], { type: 'image/png' }))
        .then(image => deliver(image,()=>image.close()))
        .catch(error => { if (current()) { this.recordPacketFailure('raster', 'browser.frame', error); lost(false, 'decode-error'); } })
        .finally(finish);
    } else if(packet.header.format === 'rgba8-zstd') {
      void this.losslessDecoder.decode(packet.payload,width*height*4)
        .then(pixels=>deliver(new ImageData(pixels,width,height),()=>{}))
        .catch(error=>{if(current()){this.recordPacketFailure('raster','browser.frame',error);lost(false,'decode-error');}})
        .finally(finish);
    } else {
      try {
        const pixels = new Uint8ClampedArray(packet.payload.buffer as ArrayBuffer, packet.payload.byteOffset, packet.payload.byteLength);
        deliver(new ImageData(pixels, width, height),()=>{});
      }
      finally { finish(); }
    }
  }

  private enqueueAudio(h: Record<string, unknown>, payload: Uint8Array): void {
    if(this.creator&&!this.creator.packetCurrent(h))return;
    this.creator?.observe(h,'accepted');
    const ctx = this.ctx.audioContext;
    this.audioDiagnostics.packets++;
    const receivedAt = performance.now();
    const arrivalGapMs=this.lastAudioArrival===null?0:receivedAt-this.lastAudioArrival;
    if (this.lastAudioArrival !== null) this.audioDiagnostics.maxArrivalGapMs = Math.max(this.audioDiagnostics.maxArrivalGapMs, arrivalGapMs);
    this.lastAudioArrival = receivedAt;
    if (ctx.state !== 'running' || this.audioClockStopped) { this.audioDiagnostics.inactiveDrops++; return; }
    const now = ctx.currentTime;
    const nativeMs=h.monotonic_ms as number;
    const channels = h.channels as number,
      frames = h.frames as number,
      rate = h.sample_rate as number;
    if (this.expectedAudioMs !== null) {
      const jump = Math.abs(nativeMs - this.expectedAudioMs);
      this.audioDiagnostics.maxNativeJumpMs = Math.max(this.audioDiagnostics.maxNativeJumpMs, jump);
      if (jump > 2) { this.audioDiagnostics.discontinuities++; this.clearAudio(); this.lastAudioArrival = receivedAt; }
    }
    // Wire continuity is independent of local admission/drops. A local queue
    // limit must not turn the following valid packet into a source discontinuity.
    const packetMs = frames / rate * 1000;
    if(arrivalGapMs>packetMs*3)this.lastLongAudioGapAt=receivedAt;
    this.expectedAudioMs=nativeMs+packetMs;
    // Comparing arrival minus native sample time removes the PCM cadence itself.
    // A source discontinuity above resets this baseline before learning again.
    const offset = receivedAt - nativeMs;
    this.audioArrivalFloor = this.audioArrivalFloor === null ? offset : Math.min(this.audioArrivalFloor, offset);
    this.audioJitterMs = Math.max(this.audioJitterMs, offset - this.audioArrivalFloor);
    if (packetMs > 200 || Math.max(0, this.audioUntil - now) + frames / rate > 0.2 + 1e-9) { this.audioDiagnostics.queueDrops++; return; } // bounded latency; drop excess audio
    this.scheduleAudio(h,payload);
  }
  private videoPendingLimit(): number {
    if (!this.mediaClock.hasAudio) return 2;
    // Audio can be queued 200 ms ahead. Preserve that many pictures plus two
    // frame margins; six pictures cannot cover a learned 175 ms reserve at60fps.
    // Rate can switch from30to60fps before the smoothed interval catches up.
    // Allocate for at least the supported60fps cadence throughout that switch.
    const interval = Math.min(1000 / 60, this.decodedIntervalMs);
    return Math.min(16, Math.ceil(200 / Math.max(1, interval)) + 2);
  }
  private playoutReserve(packetMs: number, underrun = false): number {
    // Two packet durations cover one delayed callback plus its catch-up packet.
    // Lossless pictures share the ordered local transport with PCM. A 10 ms
    // packet is not a delivery deadline: native output can coalesce up to
    // ~85 ms of stereo 48 kHz PCM while writing a picture. Start that path with
    // 100 ms protection, still inside the existing 200 ms total queue budget.
    // Learn transport jitter without stretching healthy PCM. Only a new binding
    // applies reserve; an actual underrun grows it by at least one packet.
    const maximum = Math.max(0, 200 - packetMs);
    this.audioReserveMs = Math.min(maximum, Math.max(
      this.settings.videoTransport === 'lossless' ? 100 : 0,
      packetMs * 2, this.audioJitterMs + packetMs,
      this.audioReserveMs + (underrun ? packetMs : 0),
    ));
    return this.audioReserveMs / 1000;
  }
  private scheduleAudio(h:Record<string,unknown>,payload:Uint8Array,rebase=false):void {
    if(this.creator&&!this.creator.packetCurrent(h))return;
    const ctx=this.ctx.audioContext,now=ctx.currentTime;
    if(ctx.state!=='running'||this.audioClockStopped)return;
    if(!rebase&&this.mediaClock.hasAudio&&this.audioUntil<=now) {
      // Delivery can resume as a burst after the main thread stalls. Coalesce
      // recovery over one PCM packet duration so a compositor tick between
      // two WebSocket delivery tasks cannot admit the old burst head. This
      // reserve applies to both tracks through their new native PTS binding.
      if (!this.pendingAudioRebase) {
        this.audioDiagnostics.underruns++;
        this.playoutReserve(Number(h.frames) / Number(h.sample_rate) * 1000, true);
      }
      this.pendingAudioRebase={receivedAt:performance.now(),started:this.pendingAudioRebase?.started??performance.now(),deadline:this.pendingAudioRebase?.deadline??performance.now()+Number(h.frames)/Number(h.sample_rate)*1000,header:h,payload};return;
    }
    const channels=h.channels as number,frames=h.frames as number,rate=h.sample_rate as number;
    const buffer = ctx.createBuffer(channels, frames, rate);
    const data = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
    for (let ch = 0; ch < channels; ch++)
      for (let i = 0; i < frames; i++)
        buffer.getChannelData(ch)[i] = Math.max(-1, Math.min(1, data.getFloat32((i * channels + ch) * 4, true)));
    const node = ctx.createBufferSource();
    node.buffer = buffer;
    node.connect(this.gain);
    this.scheduled.add(node);
    node.onended = () => {
      this.scheduled.delete(node);
      node.disconnect();
    };
    const target = this.mediaClock.audioTime(h.monotonic_ms as number, performance.now(), now);
    // Drop obsolete packets while playing; an expired binding must be rebased
    // instead, otherwise a persistent increase in delivery delay never recovers.
    const discontinuity=this.audioUntil<=now;
    if (!discontinuity && target + frames / rate < now - 0.02) {this.audioDiagnostics.obsoleteDrops++;node.disconnect(); this.scheduled.delete(node); return;}
    // One sample clock owns both media. Arrival/PTS jitter cannot insert gaps
    // between PCM buffers. On underrun rebase the video deadlines together.
    const at = discontinuity ? now + this.playoutReserve(frames / rate * 1000) : this.audioUntil;
    const duration=frames/rate;
    // A rare delayed delivery may need a larger reserve to recover, but that
    // delay must not become permanent live latency. After a stable window,
    // drain only queue ABOVE the learned protection. Draining back to three
    // packets consumed that protection and caused each recurring delivery
    // stall to underrun again. PCM edges and video retain one sample clock.
    const catchup=!discontinuity&&this.settings.videoTransport==='lossless'&&
      this.audioDiagnostics.rebases>0&&performance.now()-this.lastLongAudioGapAt>=3000&&
      (at-now)*1000>Math.max(this.audioReserveMs,this.audioJitterMs+duration*1000)+1;
    const playbackRate=catchup?1.003:1;
    node.playbackRate.value=playbackRate;
    this.mediaClock.bindAudio(h.monotonic_ms as number,at,discontinuity);
    if(this.creator&&!this.creator.packetCurrent(h)){node.disconnect();this.scheduled.delete(node);return;}
    node.start(at);
    this.creator?.observe(h,'scheduled');
    const observation=this.creator?.observePcm();
    if(observation)this.creator?.schedules.schedule(observation,at*ctx.sampleRate,(at+duration/playbackRate)*ctx.sampleRate,frames,playbackRate);
    this.audioDiagnostics.scheduled++;
    if (rebase) {this.audioDiagnostics.rebases++;this.lastLongAudioGapAt=performance.now();}
    this.audioUntil = at + duration/playbackRate;
    this.setAudio(this.gain);
  }
  protected release(): void {
    this.creator?.retire();
    this.ctx.audioContext.removeEventListener?.('statechange',this.audioStateChanged);
    this.unsubscribe();
    this.lifetime.abort();
    this.clearConnection();
    this.gain.disconnect();
  }
}
export const browserDefinition: SourceTypeDefinition<BrowserSettings> = {
  kind: 'browser',
  displayName: 'Browser / HTML',
  category: 'network',
  caps: { video: true, audio: true, retry: true },
  duplicate: 'copy',
  defaults: () => ({ url: 'https://example.com', width: 1920, height: 1080, videoTransport: 'auto' }),
  properties: () => [
    {
      key: 'url',
      type: 'text',
      label: 'Page URL',
      description: 'HTTP(S), or omp-html://import/index.html after choosing an HTML folder in ompeer-tools.',
    },
    { key: 'width', type: 'number', label: 'Width', min: 1, max: 3840, step: 1 },
    { key: 'height', type: 'number', label: 'Height', min: 1, max: 2160, step: 1 },
    {
      key: 'videoTransport', type: 'select', label: 'Local video quality',
      options: [{value: 'auto', label: 'Automatic'}, {value: 'lossless', label: 'Lossless (higher resource use)'}, {value: 'h264', label: 'H.264 (lower resource use)'}],
      description: 'Lossless preserves page detail before composition. H.264 uses less local bandwidth. Broadcast quality is configured separately.',
    },
    { key: 'liveTransport', type: 'info', label: 'Video transport', liveKey: 'transport' },
    { key: 'liveDecoded', type: 'info', label: 'Decoded pictures', liveKey: 'decodedFrames' },
    { key: 'liveRecovery', type: 'info', label: 'Decoder recoveries', liveKey: 'recoveries' },
    { key: 'diagnosticVideoPresentation', type: 'info', label: 'Video presentation', liveKey: 'videoPresentation', section: 'Browser diagnostics' },
    { key: 'diagnosticAudioFlow', type: 'info', label: 'PCM received / scheduled / queued', liveKey: 'audioFlow', section: 'Browser diagnostics' },
    { key: 'diagnosticAudioRecovery', type: 'info', label: 'PCM recovery', liveKey: 'audioRecovery', section: 'Browser diagnostics' },
    { key: 'diagnosticAudioDrops', type: 'info', label: 'PCM drops', liveKey: 'audioDrops', section: 'Browser diagnostics' },
    { key: 'diagnosticAudioTiming', type: 'info', label: 'Largest PCM timing changes', liveKey: 'audioTiming', section: 'Browser diagnostics' },
    { key: 'diagnosticConnection', type: 'info', label: 'Connection lifecycle', liveKey: 'connectionDiagnostic', section: 'Browser diagnostics' },
    { key: 'diagnosticSample', type: 'info', label: 'Sample time (page ms)', liveKey: 'diagnosticsSampleMs', section: 'Browser diagnostics' },
    { key: 'reload', type: 'button', label: 'Reload page', action: 'reload' },
  ],
  create: (ctx, settings) => new BrowserRuntime(ctx, settings),
};
